import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { getExperimentsConfig, getIsFetching, getUserId } from 'store/experiments/selectors';
import type { ExperimentAssignment } from 'store/experiments/types';

const SERVER_AB_ASSIGNMENTS_KEY = 'server_ab_assignments';

interface ExperimentContextValue {
  assignments: ExperimentAssignment;
  isReady: boolean;
}

const ExperimentContext = createContext<ExperimentContextValue | undefined>(undefined);

interface ExperimentProviderProps {
  children: React.ReactNode;
  environment?: string;
  serverAssignments?: ExperimentAssignment;
}

export const ExperimentProvider: React.FC<ExperimentProviderProps> = ({
  children,
  environment = process.env.NODE_ENV || 'development',
  serverAssignments,
}) => {
  const isFetching = useSelector(getIsFetching);
  const config = useSelector(getExperimentsConfig);
  const userId = useSelector(getUserId);

  // Initialize assignments with server assignments or stored assignments
  const [assignments, setAssignments] = useState<ExperimentAssignment>(() => {
    if (serverAssignments) {
      return serverAssignments;
    }

    // For client navigation, check localStorage (only in browser)
    if (typeof window !== 'undefined') {
      try {
        const storedServerAssignments = localStorage.getItem(SERVER_AB_ASSIGNMENTS_KEY);
        if (storedServerAssignments) {
          return JSON.parse(storedServerAssignments);
        }
      } catch {}
    }
    return {};
  });

  const [isClientReady, setClientReady] = useState(() => {
    if (serverAssignments) {
      return true;
    }
    // For client navigation, check if we have stored assignments
    if (typeof window !== 'undefined') {
      try {
        const storedServerAssignments = localStorage.getItem(SERVER_AB_ASSIGNMENTS_KEY);
        return !!storedServerAssignments;
      } catch {
        return false;
      }
    }
    return false;
  });

  const assignmentLength = Object.keys(assignments).length;
  useEffect(() => {
    if (typeof window !== 'undefined' && window.newrelic) {
      window.newrelic.recordCustomEvent('ABTestingInitialized', {
        userId,
        totalActiveExperiments: assignmentLength,
      });
    }
  }, [userId, assignmentLength]);

  useEffect(() => {
    // (initial page load) - store in localStorage
    if (serverAssignments) {
      try {
        localStorage.setItem(SERVER_AB_ASSIGNMENTS_KEY, JSON.stringify(serverAssignments));
      } catch {}
      return;
    }
    if (!config || isFetching) {
      return;
    }

    if (Object.keys(assignments).length === 0) {
      // No assignments available - useExperiment will fallback to 'control'
      setClientReady(true);
    }
  }, [assignments, config, isFetching, environment, serverAssignments]);

  const contextValue: ExperimentContextValue = {
    assignments,
    isReady: isClientReady,
  };

  return (
    <ExperimentContext.Provider value={contextValue}>
      {children}
      {/* TODO: Add DataLayer component for analytics integration */}
    </ExperimentContext.Provider>
  );
};

/**
 * Hook to access experiment context
 * Must be used within an ExperimentProvider
 */
export const useExperimentContext = (): ExperimentContextValue => {
  const context = useContext(ExperimentContext);
  if (!context) {
    throw new Error('useExperimentContext must be used within an ExperimentProvider');
  }
  return context;
};

/**
 * Hook to get assigned variant for a specific experiment
 * @param experimentKey - The experiment identifier
 * @returns Object with variant and isReady status
 */
export const useExperiment = (experimentKey: string): { variant: string; isReady: boolean } => {
  const { assignments, isReady } = useExperimentContext();

  // Default to 'control' if not ready or experiment key is not found
  const variant = assignments[experimentKey] || 'control';

  return { variant, isReady };
};

export default ExperimentContext;
